// @flow

import * as React from "react";

import Button from "../Button";
import FileWidget from "../FileWidget";
import { FlashMessageConsumer } from "../FlashMessages";

import UploadIcon from "../../assets/icons/file/upload.svg";
import CheckIcon from "../../assets/icons/file/download-checkmark.svg";
import { type IndicatorStyle, type DropZoneEvent } from "../FileWidget/FileWidget";
import { type ReactElementRef } from "../../types/react";

import styles from "./UploadWidget.scss";

const getInputId = name => `upload-${name}`;

type FileReaderProgressEvent = {
  ...ProgressEvent,
  target: FileReader
};

type UploadStatus = "not_uploaded" | "selected" | "uploaded" | "failure" | "ready_for_upload";

type LoadedFile = {|
  content: string | ArrayBuffer,
  file: File,
  markFailure: () => void,
  markSuccess: () => void,
  setStatus: UploadStatus => void,
  reset: () => void
|};

type UploadWidgetProps = {
  errorCallback: (Error | DOMError) => void,
  fileTypes: string[],
  acceptedFileTypes?: string[],
  fileTypeErrorMessage?: string,
  initialStatus: UploadStatus,
  name: string, // for generating ID - should be unique per page
  selectedCallback: LoadedFile => void,
  title: string,
  isButtonDisabled: boolean,
  filename?: ?string
};

type UploadWidgetState = {
  status: UploadStatus,
  errorMessage: ?string,
  files: File[]
};

const extractFilesFromDataTransfer = (dataTransfer: DataTransfer): File[] => {
  if (!dataTransfer) {
    return [];
  }

  // dataTransfer.items and dataTransfer.files are neither Arrays nor Array-like but they are iterable.
  // Thus, in order to be able to use Array methods on them they would need to be converted to arrays
  // first, the simplest method of which is to spread it into an empty array.
  if (dataTransfer.items) {
    const items: Array<?File> = [...dataTransfer.items].map(item => item.getAsFile());
    // $FlowFixMe – filters out falsy values but type system cannot detect it
    return items.filter(file => !!file);
  }
  if (dataTransfer.files) {
    return [...dataTransfer.files];
  }
  return [];
};

const tryLoadFile = ({ errorCallback, files, handleProgress, setState }) => {
  try {
    const reader = new FileReader();
    reader.onload = handleProgress;
    reader.readAsArrayBuffer(files[0]);
    setState({ status: "selected", errorMessage: null, files: [] });
  } catch (e) {
    setState({ status: "failure" });
    errorCallback(e);
  }
};

const mapStatusToIndicatorStyle = (status: UploadStatus) =>
  ({
    not_uploaded: "grey",
    selected: "yellow",
    uploaded: "grey",
    failure: "red",
    ready_for_upload: "green"
  }: { [UploadStatus]: IndicatorStyle })[status];

class UploadWidget extends React.Component<UploadWidgetProps, UploadWidgetState> {
  inputRef: ReactElementRef<HTMLInputElement> = { current: null };

  removeInputChangeListener: null | (() => void) = null;

  constructor(props: UploadWidgetProps) {
    super(props);

    this.inputRef = React.createRef();
    this.state = { status: props.initialStatus, errorMessage: null, files: [] };
  }

  componentDidMount() {
    if (this.inputRef.current === null) {
      return;
    }

    const input = this.inputRef.current;

    const handleInputChange = () => {
      const { files } = input;

      if (!(files && files.length > 0)) {
        return;
      }

      this.setState({ files });
    };

    input.addEventListener("change", handleInputChange);
    this.removeInputChangeListener = () => input.removeEventListener("change", handleInputChange);
  }

  componentWillUnmount() {
    if (this.removeInputChangeListener) {
      this.removeInputChangeListener();
    }
  }

  handleSuccessfullUpload = () => {
    const { errorCallback } = this.props;
    const { files } = this.state;
    const setState = s => this.setState(s);

    if (files.length === 0) {
      return;
    }

    if (!this.validateFileType(files[0])) {
      return;
    }

    tryLoadFile({
      errorCallback,
      files,
      setState,
      handleProgress: this.handleReaderProgress(files)
    });
  };

  handleReaderProgress =
    (files: FileList | File[]) =>
    ({ target: { DONE, error, readyState, result } }: FileReaderProgressEvent) => {
      const { errorCallback, initialStatus, selectedCallback } = this.props;
      const file = files[0];
      // bail out if not done yet
      if (readyState !== DONE) {
        return;
      }

      if (error) {
        this.setState({ status: "failure" });
        errorCallback(error);
      } else {
        this.setState({ status: "selected" });
        selectedCallback({
          content: result,
          file,
          markFailure: (errorMessage?: string) =>
            this.setState({ status: "failure", errorMessage }),
          markSuccess: () => this.setState({ status: "uploaded" }),
          setStatus: status => this.setState({ status }),
          reset: newStatus => {
            const input = this.inputRef.current;
            this.setState({ status: newStatus || initialStatus });
            input.value = "";
            if (!/safari/i.test(navigator.userAgent)) {
              input.type = "";
              input.type = "file";
            }
          }
        });
      }
    };

  validateFileType = (file: File) => {
    const { acceptedFileTypes, fileTypeErrorMessage } = this.props;
    if (!acceptedFileTypes || acceptedFileTypes.length === 0) {
      return true;
    }
    if (acceptedFileTypes.includes(file.type)) {
      return true;
    }
    // Fallback: some browsers (e.g. Windows Explorer) set file.type to an empty string.
    // Check the file extension against accepted MIME types as a second line of defense.
    if (file.type === "") {
      const extension = file.name.split(".").pop()?.toLowerCase();
      const extensionMimeMap = {
        pdf: "application/pdf",
        docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        doc: "application/msword",
        xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        xls: "application/vnd.ms-excel",
        jpg: "image/jpeg",
        jpeg: "image/jpeg",
        png: "image/png",
        bmp: "image/bmp",
        tiff: "image/tiff"
      };
      if (
        extension &&
        extensionMimeMap[extension] &&
        acceptedFileTypes.includes(extensionMimeMap[extension])
      ) {
        return true;
      }
    }
    this.setState({
      status: "failure",
      errorMessage: fileTypeErrorMessage || "Das hochgeladene Dateiformat wird nicht unterstützt."
    });
    return false;
  };

  handleFileChange = () => {
    if (this.inputRef.current === null) {
      return;
    }

    this.inputRef.current.click();
  };

  handleDrop = (onError: string => void) => (event: DropZoneEvent) => {
    const files: File[] = extractFilesFromDataTransfer(event.dataTransfer);

    if (files.length === 0) {
      return;
    }

    if (files.length > 1) {
      onError("Bitte laden Sie nicht mehr als eine Datei gleichzeitig hoch.");
      return;
    }

    if (!this.validateFileType(files[0])) {
      return;
    }

    this.setState({ files });
  };

  render() {
    const { fileTypes, name, title, isButtonDisabled, filename } = this.props;
    const { status, errorMessage } = this.state;

    const icon = filename ? CheckIcon : UploadIcon;

    return (
      <div className={styles.uploadWidget}>
        <FlashMessageConsumer>
          {({ addMessage }) => (
            <FileWidget
              cta={() => (
                <div>
                  <input
                    ref={this.inputRef}
                    className={styles.fileInput}
                    type="file"
                    id={getInputId(name)}
                    accept={fileTypes.join(",")}
                    onChange={this.handleSuccessfullUpload}
                  />
                  <Button
                    size="small"
                    onClick={this.handleFileChange}
                    isDisabled={isButtonDisabled}
                  >
                    Jetzt hochladen
                  </Button>
                </div>
              )}
              icon={icon}
              indicatorStyle={mapStatusToIndicatorStyle(status)}
              shouldShowPreloader={status === "selected"}
              onDrop={this.handleDrop(msg => addMessage(msg, "failure"))}
              title={title}
              filename={filename}
            />
          )}
        </FlashMessageConsumer>
        <div className={styles.error}>{errorMessage}</div>
      </div>
    );
  }
}

export default UploadWidget;
